import { useCallback, useEffect, useRef, useState } from "react";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    DriveError,
    MAX_DRIVE_FILE_BYTES,
    ensureSpecialFolder,
    grantDriveAccess,
    uploadToDrive,
    type DriveItem,
    type DriveShareSource,
    type UploadHandle
} from "@/lib/drive";
import { fileMarkerHtml } from "@/lib/fileMarkers";

/** What the people in the conversation may do with a file you send (Teams' "People in this chat can edit"). */
export type SharePermission = "edit" | "view" | "view-no-download";

export const PERMISSION_LABELS: Record<SharePermission, string> = {
    edit: "People in this chat can edit",
    view: "Can view",
    "view-no-download": "Can view — no download"
};

export interface PendingFile {
    key: string;
    file: File;
    name: string;
    size: number;
    type: string;
    /** 0..1 */
    progress: number;
    status: "uploading" | "done" | "error";
    error?: string;
    item?: DriveItem;
    permission: SharePermission;
    /** Part of a send that is in flight — can't be removed or re-permissioned. */
    locked: boolean;
}

export interface DriveAttachments {
    files: PendingFile[];
    uploading: boolean;
    addFiles: (files: File[] | FileList) => void;
    remove: (key: string) => void;
    retry: (key: string) => void;
    setPermission: (key: string, permission: SharePermission) => void;
    clear: () => void;
    /**
     * Wait for this batch's uploads, give the conversation's people access,
     * and return the message markers. The batch is locked until `commit`
     * (sent — drop the chips) or `release` (send failed — keep them).
     */
    prepareSend: (opts: {
        emails: string[];
        ownerEmail: string;
        source: DriveShareSource;
        sourceRef: string;
        /** Refuse to send when nobody else can be found to share with (chats); channels may be just you. */
        requireRecipients?: boolean;
    }) => Promise<{ markersHtml: string; keys: string[] }>;
    commit: (keys: string[]) => void;
    release: (keys: string[]) => void;
}

function newKey(): string {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function messageOf(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Files attached in a composer, Teams-style: each one starts uploading into
 * the sender's "Connect Chat Files" folder in Drive the moment it's attached
 * (with progress and cancel), and is shared with the conversation on send.
 */
export function useDriveAttachments(defaultPermission: SharePermission): DriveAttachments {
    const token = useAccessToken();
    const tokenRef = useRef(token);
    tokenRef.current = token;

    const [files, setFiles] = useState<PendingFile[]>([]);
    // The source of truth, readable from async code without stale closures.
    const filesRef = useRef<PendingFile[]>([]);
    const handles = useRef(new Map<string, UploadHandle>());
    const done = useRef(new Map<string, Promise<void>>());
    const folder = useRef<Promise<DriveItem> | null>(null);

    const update = useCallback((fn: (prev: PendingFile[]) => PendingFile[]) => {
        filesRef.current = fn(filesRef.current);
        setFiles(filesRef.current);
    }, []);
    const patch = useCallback((key: string, changes: Partial<PendingFile>) => {
        update((prev) => prev.map((f) => (f.key === key ? { ...f, ...changes } : f)));
    }, [update]);
    const exists = (key: string) => filesRef.current.some((f) => f.key === key);

    const startUpload = useCallback((key: string, file: File) => {
        const run = async () => {
            try {
                folder.current ??= ensureSpecialFolder("chat", tokenRef.current);
                let target: DriveItem;
                try {
                    target = await folder.current;
                } catch (err) {
                    folder.current = null;
                    throw err;
                }
                if (!exists(key)) return;
                const handle = uploadToDrive(file, target.id, tokenRef.current, (progress) => patch(key, { progress }));
                handles.current.set(key, handle);
                const item = await handle.promise;
                handles.current.delete(key);
                if (exists(key)) patch(key, { status: "done", item, progress: 1, error: undefined });
            } catch (err) {
                handles.current.delete(key);
                if (!exists(key)) return;
                if (err instanceof DriveError && err.status === -1) return;
                patch(key, { status: "error", error: messageOf(err, "Upload failed") });
            }
        };
        done.current.set(key, run());
    }, [patch]);

    const addFiles = useCallback((list: File[] | FileList) => {
        const incoming = Array.from(list);
        if (incoming.length === 0) return;
        const added: PendingFile[] = incoming.map((file) => {
            const tooBig = file.size > MAX_DRIVE_FILE_BYTES;
            return {
                key: newKey(),
                file,
                name: file.name || "Pasted file",
                size: file.size,
                type: file.type,
                progress: 0,
                status: tooBig ? "error" : "uploading",
                error: tooBig ? "Larger than 1 GB — Drive can't take it." : undefined,
                permission: defaultPermission,
                locked: false
            };
        });
        update((prev) => [...prev, ...added]);
        for (const f of added) if (f.status === "uploading") startUpload(f.key, f.file);
    }, [defaultPermission, startUpload, update]);

    const remove = useCallback((key: string) => {
        const f = filesRef.current.find((x) => x.key === key);
        if (!f || f.locked) return;
        handles.current.get(key)?.abort();
        handles.current.delete(key);
        done.current.delete(key);
        update((prev) => prev.filter((x) => x.key !== key));
    }, [update]);

    const retry = useCallback((key: string) => {
        const f = filesRef.current.find((x) => x.key === key);
        if (!f || f.locked || f.status !== "error" || f.size > MAX_DRIVE_FILE_BYTES) return;
        patch(key, { status: "uploading", progress: 0, error: undefined });
        startUpload(key, f.file);
    }, [patch, startUpload]);

    const setPermission = useCallback((key: string, permission: SharePermission) => {
        update((prev) => prev.map((f) => (f.key === key && !f.locked ? { ...f, permission } : f)));
    }, [update]);

    const clear = useCallback(() => {
        for (const f of filesRef.current) {
            if (f.locked) continue;
            handles.current.get(f.key)?.abort();
            handles.current.delete(f.key);
            done.current.delete(f.key);
        }
        update((prev) => prev.filter((f) => f.locked));
    }, [update]);

    const setLocked = useCallback((keys: string[], locked: boolean) => {
        update((prev) => prev.map((f) => (keys.includes(f.key) ? { ...f, locked } : f)));
    }, [update]);

    const prepareSend: DriveAttachments["prepareSend"] = useCallback(async ({ emails, ownerEmail, source, sourceRef, requireRecipients }) => {
        const keys = filesRef.current.filter((f) => !f.locked).map((f) => f.key);
        if (keys.length === 0) return { markersHtml: "", keys };
        setLocked(keys, true);
        try {
            await Promise.all(keys.map((k) => done.current.get(k) ?? Promise.resolve()));
            const batch = filesRef.current.filter((f) => keys.includes(f.key));
            const failed = batch.find((f) => f.status !== "done" || !f.item);
            if (failed) {
                throw new Error(`"${failed.name}" didn't upload${failed.error ? `: ${failed.error}` : ""}. Retry or remove it, then send again.`);
            }
            const me = ownerEmail.trim().toLowerCase();
            const recipients = Array.from(new Set(
                emails.map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@") && e !== me)
            ));
            if (recipients.length === 0 && requireRecipients) {
                throw new Error("Couldn't work out who is in this chat, so the files weren't shared. Try again in a moment.");
            }
            if (recipients.length > 0) {
                await Promise.all(batch.map(async (f) => {
                    try {
                        await grantDriveAccess(f.item!.id, {
                            emails: recipients,
                            role: f.permission === "edit" ? "editor" : "viewer",
                            allowDownload: f.permission !== "view-no-download",
                            source,
                            sourceRef
                        }, tokenRef.current);
                    } catch (err) {
                        throw new Error(`Couldn't share "${f.name}": ${messageOf(err, "Drive refused the permission")}`);
                    }
                }));
            }
            const markersHtml = batch.map((f) => fileMarkerHtml({
                id: f.item!.id,
                name: f.item!.name,
                size: f.item!.sizeBytes,
                type: f.item!.contentType ?? f.type,
                owner: me
            })).join("");
            return { markersHtml, keys };
        } catch (err) {
            setLocked(keys, false);
            throw err;
        }
    }, [setLocked]);

    const commit = useCallback((keys: string[]) => {
        for (const k of keys) {
            handles.current.delete(k);
            done.current.delete(k);
        }
        update((prev) => prev.filter((f) => !keys.includes(f.key)));
    }, [update]);

    const release = useCallback((keys: string[]) => setLocked(keys, false), [setLocked]);

    // Leaving the conversation cancels uploads nobody sent (an in-flight send keeps going).
    useEffect(() => () => {
        for (const f of filesRef.current) if (!f.locked) handles.current.get(f.key)?.abort();
    }, []);

    return {
        files,
        uploading: files.some((f) => f.status === "uploading"),
        addFiles,
        remove,
        retry,
        setPermission,
        clear,
        prepareSend,
        commit,
        release
    };
}

/**
 * Drag-and-drop target for files (a whole thread or composer). Counts nested
 * enter/leave pairs so the overlay doesn't flicker over child elements.
 */
export function useFileDropZone(onFiles: (files: File[]) => void) {
    const [isDragging, setIsDragging] = useState(false);
    const depth = useRef(0);
    const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
    return {
        isDragging,
        dropProps: {
            onDragEnter: (e: React.DragEvent) => {
                if (!hasFiles(e)) return;
                depth.current += 1;
                setIsDragging(true);
            },
            onDragLeave: (e: React.DragEvent) => {
                if (!hasFiles(e)) return;
                depth.current = Math.max(0, depth.current - 1);
                if (depth.current === 0) setIsDragging(false);
            },
            onDragOver: (e: React.DragEvent) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
            },
            onDrop: (e: React.DragEvent) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                depth.current = 0;
                setIsDragging(false);
                const list = Array.from(e.dataTransfer.files ?? []);
                if (list.length > 0) onFiles(list);
            }
        }
    };
}

/** Files on the clipboard (a pasted screenshot, or files copied in Explorer/Finder). */
export function filesFromPaste(e: React.ClipboardEvent): File[] {
    const out: File[] = [];
    for (const item of Array.from(e.clipboardData?.items ?? [])) {
        if (item.kind !== "file") continue;
        const f = item.getAsFile();
        if (!f) continue;
        // Screenshots arrive as "image.png"; give them a findable name.
        if (/^image\.(png|jpe?g|gif|webp)$/i.test(f.name)) {
            const ext = f.name.split(".").pop() ?? "png";
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            out.push(new File([f], `Pasted image ${stamp}.${ext}`, { type: f.type }));
        } else {
            out.push(f);
        }
    }
    return out;
}
